// @ts-nocheck
import { useState, useCallback, useRef, useEffect } from 'react';

import useConstantAttributes from './useConstantAttributes';

const usePaginatedSchemaData = (constantId) => {
  const { getConstantAttributeSchemaPaginated } = useConstantAttributes();

  const [paginatedData, setPaginatedData] = useState({
    pages: new Map(), // Map<pageNumber, pageData>
    totalRows: 0,
    totalPages: 0,
    loadedPages: new Set(),
    isLoading: false,
    loadingPage: null,
    hasError: false,
    error: null,
  });

  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const loadingPagesRef = useRef(new Set()); // Track which pages are currently loading

  // Reset data when constantId changes
  useEffect(() => {
    if (constantId) {
      setPaginatedData({
        pages: new Map(),
        totalRows: 0,
        totalPages: 0,
        loadedPages: new Set(),
        isLoading: false,
        loadingPage: null,
        hasError: false,
        error: null,
      });
      loadingRef.current = false;
      loadingPagesRef.current.clear(); // Clear loading pages tracking
    }
  }, [constantId]);

  const loadPage = useCallback(
    async (page) => {
      if (
        !constantId ||
        loadingPagesRef.current.has(page) ||
        paginatedData.loadedPages.has(page)
      ) {
        return;
      }

      // Add to loading pages
      loadingPagesRef.current.add(page);

      setPaginatedData((prev) => ({
        ...prev,
        isLoading: true,
        loadingPage: page,
        hasError: false,
        error: null,
      }));

      try {
        const response = await getConstantAttributeSchemaPaginated({
          constant_id: constantId,
          page,
          page_size: 100,
        });

        if (response?.data) {
          const {
            rows = 0,
            columns = 0,
            total_pages = 0,
            cells = {},
            mergedCells = [],
            rowSettings = {},
            columnSettings = {},
          } = response.data;

          // The data is the complete schema object for this page
          const pageSchemaData = {
            cells,
            mergedCells,
            rowSettings,
            columnSettings,
            rows,
            columns,
          };

          setPaginatedData((prev) => {
            const newPages = new Map(prev.pages);
            newPages.set(page, pageSchemaData);

            const newLoadedPages = new Set(prev.loadedPages);
            newLoadedPages.add(page);

            return {
              ...prev,
              pages: newPages,
              totalRows: rows,
              totalPages: total_pages,
              loadedPages: newLoadedPages,
              isLoading: false,
              loadingPage: null,
            };
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Failed to load page ${page}:`, error);
          setPaginatedData((prev) => ({
            ...prev,
            isLoading: false,
            loadingPage: null,
            hasError: true,
            error: error.message || 'Failed to load page data',
          }));
        }
      } finally {
        // Remove from loading pages
        loadingPagesRef.current.delete(page);
        loadingRef.current = false;
      }
    },
    [constantId, getConstantAttributeSchemaPaginated]
  );

  const loadInitialPage = useCallback(() => {
    if (constantId && !paginatedData.loadedPages.has(1)) {
      loadPage(1);
    }
  }, [constantId, loadPage, paginatedData.loadedPages]);

  const preloadNextPage = useCallback(
    (currentPage) => {
      const nextPage = currentPage + 1;
      if (
        nextPage <= paginatedData.totalPages &&
        !paginatedData.loadedPages.has(nextPage) &&
        !loadingRef.current
      ) {
        loadPage(nextPage);
      }
    },
    [loadPage, paginatedData.totalPages, paginatedData.loadedPages]
  );

  const getRowData = useCallback(
    (_rowIndex) => {
      // This function is kept for compatibility but not used in schema-based approach
      // The table grid will use the merged schema directly
      return null;
    },
    [paginatedData.pages]
  );

  const getMergedSchema = useCallback(() => {
    // Merge all loaded page schemas into a single schema
    const mergedCells = {};
    const mergedRowSettings = {};
    const mergedColumnSettings = {};
    let mergedMergedCells = [];
    let totalRows = 0;
    let totalColumns = 0;

    // Combine data from all loaded pages
    for (const [, pageSchema] of paginatedData.pages) {
      if (pageSchema?.cells) {
        // Merge cells
        Object.assign(mergedCells, pageSchema.cells);

        // Merge settings
        Object.assign(mergedRowSettings, pageSchema?.rowSettings || {});
        Object.assign(mergedColumnSettings, pageSchema?.columnSettings || {});

        // Merge merged cells
        if (pageSchema?.mergedCells && pageSchema.mergedCells.length > 0) {
          mergedMergedCells = [...mergedMergedCells, ...pageSchema.mergedCells];
        }

        // Update totals (use the largest values found)
        totalRows = Math.max(totalRows, pageSchema.rows || 0);
        totalColumns = Math.max(totalColumns, pageSchema.columns || 0);
      }
    }

    // Use pagination totals if they're larger
    totalRows = Math.max(totalRows, paginatedData.totalRows || 0);

    return {
      cells: mergedCells,
      mergedCells: mergedMergedCells,
      rowSettings: mergedRowSettings,
      columnSettings: mergedColumnSettings,
      rows: totalRows,
      columns: totalColumns,
    };
  }, [paginatedData.pages, paginatedData.totalRows]);

  const isRowLoaded = useCallback(
    (rowIndex) => {
      const page = Math.ceil((rowIndex + 1) / 100);
      return paginatedData.loadedPages.has(page);
    },
    [paginatedData.loadedPages]
  );

  const shouldLoadPage = useCallback(
    (rowIndex) => {
      const page = Math.ceil((rowIndex + 1) / 100);
      return !paginatedData.loadedPages.has(page) && !loadingRef.current;
    },
    [paginatedData.loadedPages]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    paginatedData,
    loadPage,
    loadInitialPage,
    preloadNextPage,
    getRowData,
    getMergedSchema,
    isRowLoaded,
    shouldLoadPage,
  };
};

export default usePaginatedSchemaData;
